/* Universo Grocer — storefront behavior. Plain JavaScript, no dependencies.
   Everything works without it (real links and forms); this layer makes it quicker and kinder. */
(() => {
  const UB = window.UB || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const store = {
    get(k, d) { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch (e) { return d; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} }
  };
  const money = cents => {
    const fmt = UB.moneyFormat || '${{amount}}';
    const n = (cents / 100).toFixed(2), [i, d] = n.split('.');
    const withCommas = i.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => k === 'amount_no_decimals' ? withCommas : k === 'amount_with_comma_separator' ? `${i}.${d}`.replace('.', ',') : `${withCommas}.${d}`);
  };
  const html = s => { const t = document.createElement('template'); t.innerHTML = s.trim(); return t.content; };
  const fetchSection = async (url, id) => {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', id);
    const r = await fetch(u, { credentials: 'same-origin' });
    if (!r.ok) throw new Error(r.status);
    return r.text();
  };

  /* ---------- shelf-fit: crop the white margin around each pack so it stands on the shelf ---------- */
  const FIT_KEY = 'ub-fit-v2';
  const fitCache = store.get(FIT_KEY, {});
  let fitDirty = 0;
  const saveFit = () => { if (fitDirty) { const keys = Object.keys(fitCache); if (keys.length > 3000) keys.slice(0, keys.length - 3000).forEach(k => delete fitCache[k]); store.set(FIT_KEY, fitCache); fitDirty = 0; } };
  const idOf = src => (src.split('?')[0].replace(/^(https?:)?\/\/[^/]+/, '') || src);        // the file path, not just its name
  function measure(src) {
    return new Promise(res => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = () => {
        try {
          const W = 64, H = Math.max(1, Math.round(64 * im.naturalHeight / im.naturalWidth));
          const c = document.createElement('canvas'); c.width = W; c.height = H;
          const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(im, 0, 0, W, H);
          const d = x.getImageData(0, 0, W, H).data;
          let x0 = W, y0 = H, x1 = -1, y1 = -1;
          for (let y = 0; y < H; y++) for (let xx = 0; xx < W; xx++) {
            const i = (y * W + xx) * 4;
            if (d[i + 3] > 20 && (d[i] < 238 || d[i + 1] < 238 || d[i + 2] < 238)) { if (xx < x0) x0 = xx; if (xx > x1) x1 = xx; if (y < y0) y0 = y; if (y > y1) y1 = y; }
          }
          if (x1 < 0) return res(null);
          const p = 1;
          res([Math.max(0, x0 - p) / W, Math.max(0, y0 - p) / H, Math.min(W, x1 + 1 + p) / W, Math.min(H, y1 + 1 + p) / H]);
        } catch (e) { res(null); }
      };
      im.onerror = () => res(null);
      const u = new URL(src, location.href); u.searchParams.set('width', '64'); im.src = u.href;
    });
  }
  /* ---------- background removal for pack photos used in banners, tiles and circles ---------- */
  const cutCache = new Map();
  const CUT_T = 34;
  function cutout(url, box) {
    const ck = url + (box ? box.join(',') : '');
    if (cutCache.has(ck)) return cutCache.get(ck);
    const job = (async () => {
      const ckey = new URL('/__ub-cut/' + encodeURIComponent(ck), location.origin).href;
      let cache = null;
      try { cache = 'caches' in window ? await caches.open('ub-cut-v1') : null; } catch (e) {}
      try {
        if (cache) { const hit = await cache.match(ckey); if (hit) { if (hit.headers.get('x-none')) return null; return URL.createObjectURL(await hit.blob()); } }
      } catch (e) {}
      const remember = (blob) => { try { if (cache) cache.put(ckey, blob ? new Response(blob, { headers: { 'content-type': 'image/webp' } }) : new Response('', { headers: { 'x-none': '1' } })); } catch (e) {} };
      const res = await (async () => { try {
        const r = await fetch(url, { mode: 'cors', credentials: 'omit' }); if (!r.ok) return undefined;
        const bmp = await createImageBitmap(await r.blob());
        const W = bmp.width, H = bmp.height; if (W < 24 || H < 24) return null;
        const c = document.createElement('canvas'); c.width = W; c.height = H;
        const x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(bmp, 0, 0);
        const im = x.getImageData(0, 0, W, H), d = im.data;
        // the background colour is the most common colour along the border
        const bins = {}, edge = [];
        const look = (px, py) => { const i = (py * W + px) * 4; edge.push(i); const k = (d[i] >> 5) * 64 + (d[i + 1] >> 5) * 8 + (d[i + 2] >> 5); (bins[k] = bins[k] || []).push(i); };
        for (let px = 0; px < W; px++) { look(px, 0); look(px, H - 1); }
        for (let py = 1; py < H - 1; py++) { look(0, py); look(W - 1, py); }
        let best = null; for (const k in bins) if (!best || bins[k].length > best.length) best = bins[k];
        let br = 0, bg = 0, bb = 0; best.forEach(i => { br += d[i]; bg += d[i + 1]; bb += d[i + 2]; }); br /= best.length; bg /= best.length; bb /= best.length;
        const dist = i => { const a = d[i] - br, b = d[i + 1] - bg, e = d[i + 2] - bb; return Math.sqrt(a * a + b * b + e * e); };
        let near = 0; edge.forEach(i => { if (dist(i) <= CUT_T) near++; });
        if (near / edge.length < 0.6) return null;                       // no clean background to remove
        const mask = new Uint8Array(W * H), q = new Int32Array(W * H); let qh = 0, qt = 0;
        const push = p => { if (!mask[p] && dist(p * 4) <= CUT_T) { mask[p] = 1; q[qt++] = p; } };
        for (let px = 0; px < W; px++) { push(px); push((H - 1) * W + px); }
        for (let py = 0; py < H; py++) { push(py * W); push(py * W + W - 1); }
        while (qh < qt) { const p = q[qh++], px = p % W; if (px > 0) push(p - 1); if (px < W - 1) push(p + 1); if (p >= W) push(p - W); if (p < W * H - W) push(p + W); }
        const removed = qt / (W * H);
        if (removed < 0.003 || removed > 0.92) return null;              // nothing to remove, or it would eat the pack
        for (let p = 0; p < W * H; p++) {
          const i = p * 4;
          if (mask[p]) { d[i + 3] = 0; continue; }
          const px = p % W, nb = (px > 0 && mask[p - 1]) || (px < W - 1 && mask[p + 1]) || (p >= W && mask[p - W]) || (p < W * H - W && mask[p + W]);
          if (nb) { const a = Math.max(0, Math.min(1, (dist(i) - CUT_T) / (CUT_T * 0.9))); d[i + 3] = Math.round(255 * (0.35 + 0.65 * a)); }
        }
        x.putImageData(im, 0, 0);
        let out = c;
        if (box) {                                                        // crop to the pack after the background is gone
          const sx = Math.round(box[0] * W), sy = Math.round(box[1] * H), sw = Math.max(1, Math.round((box[2] - box[0]) * W)), sh = Math.max(1, Math.round((box[3] - box[1]) * H));
          out = document.createElement('canvas'); out.width = sw; out.height = sh; out.getContext('2d').drawImage(c, sx, sy, sw, sh, 0, 0, sw, sh);
        }
        return await new Promise(res => out.toBlob(b => res(b || null), 'image/webp', 0.92));
      } catch (e) { return undefined; } })();
      if (res === undefined) return null;                                 // network trouble: try again next visit
      remember(res);
      return res ? URL.createObjectURL(res) : null;
    })();
    cutCache.set(ck, job); return job;
  }
  function cutInto(img, url, box) {
    if (img.closest('[data-card],.card,[data-pdp]')) return;             // product cards in the aisle stay as they are
    cutout(url, box).then(async u => {
      if (!u) return;
      const pre = new Image(); pre.src = u;
      try { await pre.decode(); } catch (e) {}
      img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.src = u; img.classList.add('cut');
    });
  }
  function applyFit(img, box) {
    const done = () => img.classList.add('ok');
    const fullUrl = w => { const u = new URL(img.dataset.src, location.href); u.searchParams.set('width', Math.min(Math.round(w), 1000)); return u.href; };
    const plain = () => cutInto(img, fullUrl((+img.dataset.max || 360) * 2));
    if (!box) { plain(); return done(); }
    const [a, b, c, d] = box, W = +img.dataset.w, H = +img.dataset.h;
    const area = (c - a) * (d - b);
    if (!W || !H || area > 0.9) { plain(); return done(); }
    const L = Math.round(a * W), T = Math.round(b * H), CW = Math.round((c - a) * W), CH = Math.round((d - b) * H);
    const max = +img.dataset.max || 360;
    const out = w => { const u = new URL(img.dataset.src, location.href); u.searchParams.set('crop', 'region'); u.searchParams.set('crop_left', L); u.searchParams.set('crop_top', T); u.searchParams.set('crop_width', CW); u.searchParams.set('crop_height', CH); u.searchParams.set('width', Math.min(w, CW)); return u.href; };
    const pre = new Image();
    pre.onload = () => {
      if (img.classList.contains('ok')) { img.classList.remove('swap'); void img.offsetWidth; img.classList.add('swap'); }
      img.removeAttribute('srcset'); img.removeAttribute('sizes');
      img.src = pre.src; img.srcset = `${out(max)} 1x, ${out(max * 2)} 2x`;
      img.setAttribute('width', CW); img.setAttribute('height', CH);
      done();
      cutInto(img, fullUrl(max * 2 / (c - a)), box);
    };
    pre.onerror = done;
    pre.src = out(max);
  }
  const fitQueue = [];
  let fitBusy = 0;
  function pumpFit() {
    while (fitBusy < 8 && fitQueue.length) {
      const img = fitQueue.shift();
      const key = idOf(img.dataset.src);
      fitBusy++;
      measure(img.dataset.src).then(box => { fitCache[key] = box || 0; fitDirty++; applyFit(img, box); }).finally(() => { fitBusy--; pumpFit(); if (!fitQueue.length) saveFit(); });
    }
  }
  const fitIO = 'IntersectionObserver' in window ? new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { fitIO.unobserve(e.target); fitQueue.push(e.target); pumpFit(); } }), { rootMargin: '1000px 800px' }) : null;
  function fitAll(root = document) {
    $$('img.fit:not([data-fitted])', root).forEach(img => {
      img.dataset.fitted = '1';
      if (!img.dataset.src) return img.classList.add('ok');
      const key = idOf(img.dataset.src);
      if (key in fitCache) return applyFit(img, fitCache[key] || null);
      setTimeout(() => { if (img.complete && img.naturalWidth) img.classList.add('ok'); else img.addEventListener('load', () => img.classList.add('ok'), { once: true }); }, 450);
      if (fitIO) fitIO.observe(img); else { fitQueue.push(img); pumpFit(); }
    });
  }
  addEventListener('pagehide', saveFit);

  /* ---------- scrolling shelves ---------- */
  function wireRows(root = document) {
    $$('.rowtrack', root).forEach(t => {
      const w = t.closest('.rowwrap'); if (!w) return;
      const pv = $('.prev', w), nx = $('.next', w);
      const upd = () => { if (pv) pv.disabled = t.scrollLeft < 20; if (nx) nx.disabled = t.scrollLeft + t.clientWidth >= t.scrollWidth - 20; };
      if (!t.dataset.wired) { t.dataset.wired = 1; t.addEventListener('scroll', upd, { passive: true }); }
      upd();
    });
  }
  document.addEventListener('click', e => {
    const b = e.target.closest('.rbtn'); if (!b) return;
    const t = $('.rowtrack', b.closest('.rowwrap'));
    t.scrollBy({ left: +b.dataset.dir * t.clientWidth * 0.85, behavior: REDUCED ? 'auto' : 'smooth' });
  });

  /* ---------- the living shelf wall ---------- */
  function wall() {
    const w = $('[data-wall]'); if (!w) return;
    const rows = $$('.scroller', w);
    // a second copy of each shelf makes the drift seamless; it is hidden from keyboards and screen readers
    rows.forEach(r => { const t = $('.track', r); const dup = document.createElement('span'); dup.className = 'dup'; dup.setAttribute('aria-hidden', 'true');
      [...t.children].forEach(a => { const c = a.cloneNode(true); c.tabIndex = -1; dup.append(c); }); t.append(dup); });
    let hold = 0, hover = false, paused = REDUCED;
    rows.forEach(r => {
      r._x = +r.dataset.speed < 0 ? r.scrollWidth / 2 : 0; r.scrollLeft = r._x;
      ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(ev => r.addEventListener(ev, () => { hold = performance.now(); }, { passive: true }));
      r.addEventListener('scroll', () => { if (performance.now() - hold < 2600) r._x = r.scrollLeft; }, { passive: true });
    });
    w.addEventListener('mouseenter', () => hover = true);
    w.addEventListener('mouseleave', () => hover = false);
    const pb = $('[data-pause]');
    const setPause = p => {
      paused = p; if (!pb) return;
      pb.setAttribute('aria-pressed', String(p));
      $('span', pb).textContent = p ? 'Play shelves' : 'Pause shelves';
      $('svg', pb).innerHTML = p ? '<path d="M8 5l11 7-11 7z" fill="currentColor"/>' : '<path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/>';
    };
    if (pb) { pb.addEventListener('click', () => setPause(!paused)); if (REDUCED) setPause(true); }
    let visible = true, running = false, last = performance.now();
    const kick = () => { if (running) return; running = true; last = performance.now(); requestAnimationFrame(tick); };
    if ('IntersectionObserver' in window) new IntersectionObserver(es => { visible = es[0].isIntersecting; if (visible) kick(); }).observe(w);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) kick(); });
    w.addEventListener('mouseleave', kick); ['pointerdown', 'wheel', 'touchstart', 'keydown', 'focusout'].forEach(ev => w.addEventListener(ev, kick, { passive: true }));
    if (pb) pb.addEventListener('click', kick);
    function tick(now) {
      if (paused || !visible || document.hidden) { running = false; return; }
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      const idle = now - hold > 2600;
      if (!paused && !hover && idle && visible && !w.contains(document.activeElement) && document.visibilityState === 'visible') {
        rows.forEach(r => { const half = r.scrollWidth / 2; if (!half) return; let x = (r._x ?? r.scrollLeft) + (+r.dataset.speed) * dt; if (x >= half) x -= half; if (x < 0) x += half; r._x = x; r.scrollLeft = x; });
      } else if (!idle) {
        rows.forEach(r => { const half = r.scrollWidth / 2; if (half && r.scrollLeft >= half) r.scrollLeft -= half; r._x = r.scrollLeft; });
      }
      if (hover && idle) { running = false; return; }                   // resting under the pointer: wake on mouseleave
      requestAnimationFrame(tick);
    }
    kick();
  }

  /* ---------- cart ---------- */
  const cartState = { items: {} };
  let shownCents = null;
  const setTotal = cents => {
    const tb = $('[data-tb-total]'); if (tb && cents != null) tb.textContent = cents > 0 ? money(cents).replace(/\.00$/, '') : 'Cart';
    const t = $('#cartTotal'); if (!t || cents == null) return;
    const from = shownCents == null ? (parseInt(t.textContent.replace(/\D/g, ''), 10) || 0) : shownCents; shownCents = cents;
    if (REDUCED || from === cents) { t.textContent = money(cents); return; }
    const t0 = performance.now();
    (function step(n) { const k = Math.min(1, (n - t0) / 700), e = 1 - Math.pow(1 - k, 3); t.textContent = money(Math.round(from + (cents - from) * e)); if (k < 1) requestAnimationFrame(step); })(t0);
    t.classList.remove('tick'); void t.offsetWidth; t.classList.add('tick');
  };
  const setCount = n => {
    const tc = $('[data-tb-count]'); if (tc) { tc.textContent = n; tc.hidden = !n; tc.classList.remove('bump'); void tc.offsetWidth; tc.classList.add('bump'); }
    const b = $('#cartCount'); if (!b) return;
    b.textContent = n; b.hidden = !n;
    b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump');
    const oc = $('#openCart'); if (oc) oc.setAttribute('aria-label', `Cart, ${n} ${n === 1 ? 'case' : 'cases'}`);
  };
  function syncControls() {
    $$('[data-ctl]').forEach(el => {
      const id = el.dataset.ctl, card = el.closest('[data-card]'); if (!card) return;
      const q = cartState.items[id] || 0;
      const name = $('.nm', card)?.textContent.trim() || 'item';
      if (q > 0) {
        if (el.dataset.mode === 'qty' && $('.qty span', el)) { $('.qty span', el).textContent = `${q} in cart`; return; }
        el.dataset.mode = 'qty';
        el.innerHTML = `<div class="qty"><button type="button" data-dec="${id}" aria-label="Remove one of ${esc(name)}">−</button><span class="num" role="status">${q} in cart</span><button type="button" data-inc="${id}" aria-label="Add one more ${esc(name)}">+</button></div>`;
      } else if (el.dataset.mode === 'qty') {
        el.dataset.mode = '';
        el.innerHTML = `<form method="post" action="${UB.routes.cartAdd}" data-add><input type="hidden" name="id" value="${id}"><input type="hidden" name="quantity" value="1"><button class="add" type="submit" aria-label="Add ${esc(name)} to cart"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 4h2l2.4 11.2a2 2 0 002 1.6h7.9a2 2 0 002-1.5L21 8H6.2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="20" r="1.4" fill="currentColor"/><circle cx="17" cy="20" r="1.4" fill="currentColor"/></svg>Add</button></form>`;
      }
    });
  }
  async function loadCart() {
    try {
      const c = await (await fetch(UB.routes.cart + '.js', { credentials: 'same-origin' })).json();
      cartState.items = {}; c.items.forEach(i => { cartState.items[i.variant_id] = (cartState.items[i.variant_id] || 0) + i.quantity; });
      cartState.keys = {}; c.items.forEach(i => { cartState.keys[i.variant_id] = i.key; });
      setTotal(c.total_price);
      syncControls();
      return c;
    } catch (e) { return null; }
  }
  async function refreshDrawer(sectionsHtml) {
    const d = $('[data-cart-drawer]');
    let h = sectionsHtml && sectionsHtml['cart-drawer'];
    if (!h) { try { h = await fetchSection(location.pathname, 'cart-drawer'); } catch (e) { return; } }
    const frag = html(h), nd = $('[data-cart-drawer]', frag);
    if (d && nd) { d.querySelector('header').replaceWith($('header', nd)); $('.body', d).replaceWith($('.body', nd)); const f = $('footer', d), nf = $('footer', nd); if (f) f.remove(); if (nf) d.append(nf); }
    loadSuggestions();
  }
  async function add(variantId, qty, fromEl) {
    const btn = fromEl && fromEl.querySelector ? fromEl.querySelector('[type=submit]') || fromEl : null;
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch(UB.routes.cartAdd + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: +variantId, quantity: qty }], sections: 'cart-drawer', sections_url: location.pathname }) });
      const data = await r.json();
      if (!r.ok) { toastMsg(data.description || data.message || 'Sorry, we could not add that.'); return false; }
      const it = data.items && data.items[0];
      await loadCart();
      setCount(Object.values(cartState.items).reduce((a, b) => a + b, 0));
      refreshDrawer(data.sections);
      fly(fromEl);
      if (it) toastAdded(it, cartState.items[variantId] || qty);
      return true;
    } catch (e) { toastMsg('Connection problem. Please try again.'); return false; }
    finally { if (btn) btn.removeAttribute('aria-busy'); }
  }
  /* several cases at once (bundles): one request, then the cart opens */
  async function addMany(items, fromEl) {
    if (fromEl) fromEl.setAttribute('aria-busy', 'true');
    try {
      const r = await fetch(UB.routes.cartAdd + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items, sections: 'cart-drawer', sections_url: location.pathname }) });
      const data = await r.json();
      if (!r.ok) { toastMsg(data.description || data.message || 'Sorry, we could not add those.'); return false; }
      await loadCart();
      setCount(Object.values(cartState.items).reduce((a, b) => a + b, 0));
      await refreshDrawer(data.sections);
      fly(fromEl);
      openDrawer();
      return true;
    } catch (e) { toastMsg('Connection problem. Please try again.'); return false; }
    finally { if (fromEl) fromEl.removeAttribute('aria-busy'); }
  }
  async function change(key, qty) {
    try { await change2(key, qty); } catch (e) { toastMsg('Connection problem. Please try again.'); }
    finally { $$('[data-line].busy').forEach(l => l.classList.remove('busy')); }
  }
  async function change2(key, qty) {
    const r = await fetch(UB.routes.cartChange + '.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: qty, sections: 'cart-drawer', sections_url: location.pathname }) });
    const data = await r.json();
    if (!r.ok) { toastMsg(data.description || 'Sorry, that did not work.'); return; }
    setCount(data.item_count); setTotal(data.total_price);
    cartState.items = {}; cartState.keys = {}; data.items.forEach(i => { cartState.items[i.variant_id] = (cartState.items[i.variant_id] || 0) + i.quantity; cartState.keys[i.variant_id] = i.key; });
    syncControls();
    refreshDrawer(data.sections);
    if ($('[data-cart-page]')) location.reload();
  }
  let toastT;
  const toastEl = () => $('#toast');
  function toastShow(inner) { const t = toastEl(); if (!t) return; t.innerHTML = inner; t.classList.add('on'); clearTimeout(toastT); toastT = setTimeout(() => t.classList.remove('on'), 3400); }
  function toastAdded(it, n) {
    const img = it.image ? `<img src="${esc(it.image)}${it.image.includes('?') ? '&' : '?'}width=96" alt="">` : '';
    toastShow(`${img}<span><b>Added to cart</b><br><span class="num" style="opacity:.85">${n} in cart · ${esc(it.vendor || '')}</span></span><button class="btn" type="button" data-cart-open>View cart</button>`);
  }
  const toastMsg = m => toastShow(`<span>${esc(m)}</span>`);
  function fly(fromEl) {
    const oc = [$('#openCart'), $('.tabbar [data-cart-open]')].find(e => e && e.offsetParent);
    const pop = () => { if (!oc) return; oc.classList.remove('pop'); void oc.offsetWidth; oc.classList.add('pop'); };
    if (REDUCED || !fromEl) return;
    const box = fromEl.closest('[data-card],.bb,[data-pdp],.mbar,[data-spot-slide]');
    const img = box?.querySelector('.sp-main') || box?.querySelector('img') || $('.gimg');
    if (!img || !oc) return pop();
    const a = img.getBoundingClientRect(), b = oc.getBoundingClientRect(); if (!a.width || a.bottom < 0) return pop();
    const S = 84, f = img.cloneNode(); f.removeAttribute('srcset'); f.removeAttribute('loading'); f.className = 'flyer ring';
    f.style.cssText = `left:${a.left + a.width / 2 - S / 2}px;top:${a.top + a.height / 2 - S / 2}px;width:${S}px;height:${S}px`; document.body.appendChild(f);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
    f.animate([
      { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
      { transform: `translate(${dx * 0.45}px,${Math.min(0, dy) * 0.5 - 70}px) scale(.8) rotate(-10deg)`, opacity: 1, offset: 0.45 },
      { transform: `translate(${dx}px,${dy}px) scale(.28) rotate(6deg)`, opacity: .9 }
    ], { duration: 780, easing: 'cubic-bezier(.4,0,.2,1)' }).onfinish = () => { f.remove(); pop(); };
  }
  let lastFocus = null;
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const openPanels = () => [$('[data-cart-drawer]:not([hidden])'), $('[data-saved-drawer]:not([hidden])'), $('[data-fside].on'), innerWidth <= 900 ? $('[data-mega]:not([hidden])') : null].filter(Boolean);
  document.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    const p = openPanels().pop(); if (!p) return;
    const f = $$(FOCUSABLE, p).filter(el => el.offsetParent !== null || el === document.activeElement); if (!f.length) { e.preventDefault(); p.focus(); return; }
    const first = f[0], lastEl = f[f.length - 1];
    if (!p.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
    else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
  });
  const asDialog = (el, label) => { if (el && !el.hasAttribute('role')) { el.setAttribute('role', 'dialog'); el.setAttribute('aria-modal', 'true'); if (label && !el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) el.setAttribute('aria-label', label); } };
  function openDrawer() {
    const d = $('[data-cart-drawer]'); if (!d) { location.href = UB.routes.cart; return; }
    lastFocus = document.activeElement; asDialog(d, 'Your cart');
    $('[data-scrim]').hidden = false; d.hidden = false; d.focus();
    toastEl()?.classList.remove('on');
    document.documentElement.style.overflow = 'hidden';
    loadSuggestions();
  }
  function closeDrawer() {
    const d = $('[data-cart-drawer]'); if (!d || d.hidden) return;
    d.hidden = true; $('[data-scrim]').hidden = true; document.documentElement.style.overflow = '';
    if (lastFocus) lastFocus.focus();
  }
  async function loadSuggestions() {
    const s = $('[data-cart-sug]'); if (!s || s.dataset.loaded === s.dataset.for) return;
    s.dataset.loaded = s.dataset.for;
    try {
      const t = await fetchSection(`${UB.routes.recommendations}?product_id=${s.dataset.for}&limit=8&intent=related`, 'cart-suggest');
      const rows = $('[data-sug-rows]', html(t));
      if (rows) { s.innerHTML = rows.innerHTML; s.hidden = false; }
    } catch (e) {}
  }

  document.addEventListener('submit', e => {
    const f = e.target.closest('form[data-add]'); if (!f) return;
    if (e.submitter && e.submitter.closest('.shopify-payment-button')) return;
    e.preventDefault();
    const fd = new FormData(f);
    add(fd.get('id'), Math.max(1, parseInt(fd.get('quantity'), 10) || 1), f).then(ok => {
      const big = $('[data-bigadd]', f);
      if (ok && big) { big.classList.add('ok'); const keep = big.innerHTML; big.textContent = 'Added to cart ✓'; setTimeout(() => { big.classList.remove('ok'); big.innerHTML = keep; }, 1600); const n = $('[data-cnote]'); if (n) { const q = cartState.items[fd.get('id')] || 0; n.textContent = `${q} in your cart`; } }
    });
  });
  document.addEventListener('click', e => {
    const t = e.target;
    const inc = t.closest('[data-inc]'); if (inc) { e.preventDefault(); add(inc.dataset.inc, 1, inc); return; }
    const dec = t.closest('[data-dec]'); if (dec) { e.preventDefault(); const id = dec.dataset.dec, key = cartState.keys && cartState.keys[id]; if (key) change(key, Math.max(0, (cartState.items[id] || 1) - 1)); return; }
    const lq = t.closest('[data-line-q]'); if (lq) { e.preventDefault(); const line = lq.closest('[data-line]'); line.classList.add('busy'); change(line.dataset.line, +lq.dataset.lineQ); return; }
    if (t.closest('[data-cart-open]')) { if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; e.preventDefault(); openDrawer(); return; }
    if (t.closest('[data-close]') || t.closest('[data-scrim]')) { closeDrawer(); closeSaved(); closeFside(); return; }
    const sc = t.closest('[data-scroll]'); if (sc) { const el = $(sc.getAttribute('href')); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth' }); } }
  });

  document.addEventListener('keydown', e => { if (e.key === 'Escape') { closeDrawer(); closeSaved(); closeFside(); closeMega(true); closeTA(); } });

  /* ---------- predictive search ---------- */
  const q = $('#q'), ta = $('#ta');
  let taSel = -1, taTimer, taCtrl;
  function closeTA() { if (!ta) return; ta.hidden = true; if (q) { q.setAttribute('aria-expanded', 'false'); q.removeAttribute('aria-activedescendant'); } taSel = -1; }
  /* empty search box: your recent searches and a few good places to start */
  const RKEY = 'ub-recent-q';
  const recentQ = () => store.get(RKEY, []);
  const pushQ = t => { t = (t || '').trim().slice(0, 60); if (!t) return; store.set(RKEY, [t, ...recentQ().filter(x => x.toLowerCase() !== t.toLowerCase())].slice(0, 6)); };
  const sUrl = t => `${UB.routes.search}?type=product&options%5Bprefix%5D=last&options%5Bunavailable_products%5D=last&q=${encodeURIComponent(t)}`;
  function starter() {
    if (!ta || !q) return;
    if (taCtrl) taCtrl.abort();
    const rec = recentQ(), tryQ = (q.dataset.try || '').split(',').map(x => x.trim()).filter(Boolean);
    if (!rec.length && !tryQ.length) return closeTA();
    const chip = (t, ic) => `<a class="ta-chip" role="option" data-ta href="${sUrl(t)}">${ic}${esc(t)}</a>`;
    const IC_CLOCK = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="2"/><path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    const IC_SEARCH = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="2"/><path d="M16 16l4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    ta.innerHTML = (rec.length ? `<div class="ta-start" role="group" aria-label="Your recent searches"><div class="ta-h"><span>Recent searches</span><button type="button" class="link" data-clear-recent>Clear</button></div><div class="ta-chips">${rec.map(t => chip(t, IC_CLOCK)).join('')}</div></div>` : '')
      + (tryQ.length ? `<div class="ta-start" role="group" aria-label="Try searching for"><div class="ta-h"><span>Try searching for</span></div><div class="ta-chips">${tryQ.map(t => chip(t, IC_SEARCH)).join('')}</div></div>` : '');
    $$('[data-ta]', ta).forEach((o, i) => { o.id = 'ta-' + i; });
    ta.hidden = false; q.setAttribute('aria-expanded', 'true'); taSel = -1;
  }
  document.addEventListener('click', e => { if (e.target.closest('[data-clear-recent]')) { e.preventDefault(); store.set(RKEY, []); starter(); q && q.focus(); } });
  document.addEventListener('submit', e => { if (e.target.matches('#searchForm,.bigsearch,form[action*="/search"]')) { const i = e.target.querySelector('[name=q]'); if (i) pushQ(i.value); } }, true);
  try { const sp = new URLSearchParams(location.search); if (location.pathname.startsWith(UB.routes.search) && sp.get('q')) pushQ(sp.get('q')); } catch (e) {}
  async function suggest() {
    const term = q.value.trim(); if (!term) return starter();
    q.removeAttribute('aria-activedescendant');
    if (taCtrl) taCtrl.abort(); taCtrl = new AbortController();
    const form = q.closest('form'); form && form.classList.add('is-busy');
    try {
      const u = `${UB.routes.suggest}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.barcode,vendor,tag`;
      const r = await fetch(u, { signal: taCtrl.signal });
      const t = await r.text();
      const frag = html(t); const inner = frag.querySelector('.shopify-section') || frag;
      ta.innerHTML = ''; ta.append(...inner.childNodes);
      ta.hidden = false; q.setAttribute('aria-expanded', 'true'); taSel = -1;
      $$('[data-ta]', ta).forEach((o, i) => { o.id = 'ta-' + i; });
      mark(ta, term);
      form && form.classList.remove('is-busy');
    } catch (e) { if (e.name !== 'AbortError') form && form.classList.remove('is-busy'); }
  }
  /* bold the typed words inside each suggestion, so the eye finds the match */
  function mark(root, term) {
    const words = term.toLowerCase().split(/\s+/).filter(w => w.length > 1).map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!words.length) return;
    const re = new RegExp(`(${words.join('|')})`, 'gi');
    $$('.opt .t', root).forEach(t => {
      const tn = t.firstChild; if (!tn || tn.nodeType !== 3 || !re.test(tn.textContent)) return;
      re.lastIndex = 0;
      const span = document.createElement('span'); span.innerHTML = esc(tn.textContent).replace(re, '<mark>$1</mark>');
      t.replaceChild(span, tn);
    });
  }
  if (q && ta) {
    q.addEventListener('input', () => { clearTimeout(taTimer); taTimer = setTimeout(suggest, 160); });
    q.addEventListener('focus', () => { UB.showHeader && UB.showHeader(); if (!q.value.trim()) return starter(); if (ta.innerHTML) { ta.hidden = false; q.setAttribute('aria-expanded', 'true'); } });
    q.addEventListener('input', () => { if (!q.value.trim()) starter(); });
    q.addEventListener('keydown', e => {
      const opts = $$('[data-ta]', ta); if (ta.hidden || !opts.length) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); taSel = (taSel + (e.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length; opts.forEach((o, i) => o.setAttribute('aria-selected', String(i === taSel))); q.setAttribute('aria-activedescendant', opts[taSel].id); opts[taSel].scrollIntoView({ block: 'nearest' }); q.setAttribute('aria-activedescendant', opts[taSel].id); }
      else if (e.key === 'Enter' && taSel >= 0) { e.preventDefault(); location.href = opts[taSel].href; }
    });
    document.addEventListener('click', e => { if (!e.target.closest('.search')) closeTA(); });
  }
  document.addEventListener('click', e => { const b = e.target.closest('[data-shelves-more]'); if (!b) return; const nav = b.closest('.sshelves'); nav.classList.add('all'); b.remove(); $('.xtra', nav)?.focus(); });
  document.addEventListener('click', e => { if (!e.target.closest('[data-tb-search]') || !q) return; UB.showHeader && UB.showHeader(); q.focus(); q.select(); if (!q.value.trim()) starter(); });
  /* phones: the suggestions fill the space between the search box and the keyboard */
  const fitTA = () => { if (!ta || !q || innerWidth > 680) return; const b = q.closest('.box')?.getBoundingClientRect() || q.getBoundingClientRect(); const vh = window.visualViewport ? visualViewport.height : innerHeight; ta.style.setProperty('--ta-top', Math.round(b.bottom + 6) + 'px'); ta.style.setProperty('--ta-max', Math.max(160, Math.round(vh - b.bottom - 14)) + 'px'); };
  if (window.visualViewport) visualViewport.addEventListener('resize', () => { if (ta && !ta.hidden) fitTA(); });
  if (ta && 'MutationObserver' in window) new MutationObserver(() => { if (!ta.hidden) fitTA(); }).observe(ta, { attributes: true, attributeFilter: ['hidden'], childList: true });
  // the header may still be sliding back into view: place the panel again once it lands
  $('#hdr')?.closest('.sec-header')?.addEventListener('transitionend', e => { if (e.propertyName === 'transform' && ta && !ta.hidden) fitTA(); });
  document.addEventListener('keydown', e => { if (e.key === '/' && q && !e.target.matches('input,textarea,select')) { e.preventDefault(); q.focus(); } });

  /* ---------- aisle pages: auto-apply sort and filters, "show more" without leaving ---------- */
  document.addEventListener('change', e => { const s = e.target.closest('[data-autosubmit]'); if (s && s.form) s.form.requestSubmit ? s.form.requestSubmit() : s.form.submit(); });
  document.addEventListener('submit', e => {
    const f = e.target.closest('form[data-refine]'); if (!f) return;
    const fd = new FormData(f); const u = new URL(f.action, location.origin);
    for (const [k, v] of fd) if (v !== '') u.searchParams.append(k, v);
    e.preventDefault(); location.href = u.pathname + u.search + '#results';
  });
  document.addEventListener('click', async e => {
    const m = e.target.closest('[data-more]'); if (!m) return;
    e.preventDefault(); m.setAttribute('aria-busy', 'true'); m.textContent = 'Loading…';
    try {
      const r = await fetch(m.href); const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const cards = $$('[data-grid] > *', doc); let grid = $('[data-grid]');
      const res = $('[data-results]');
      if (res && $('[data-results]', doc)) {
        // search results grouped by aisle: each new card joins its aisle, a new aisle joins the end
        const first = [];
        $$('[data-results] .sgroup', doc).forEach(sg => {
          const mine = $(`.sgroup[data-group="${sg.dataset.group}"] [data-grid]`, res), got = $$('[data-grid] > *', sg).map(c => document.importNode(c, true));
          if (mine) got.forEach(c => mine.append(c)); else { const ng = document.importNode(sg, true); res.append(ng); got.splice(0, got.length, ...$$('[data-grid] > *', ng)); }
          first.push(got[0]);
        });
        const gs = $$('.sgroup', res);
        gs.forEach(sg => { const n = $('[data-gn]', sg); if (n) n.textContent = $('[data-grid]', sg).children.length; $('.sg-head', sg).hidden = gs.length < 2; });
        res.classList.toggle('grouped', gs.length > 1);
        grid = res;
        first[0] && $('a.nm', first[0])?.focus({ preventScroll: true });
      } else cards.forEach(c => grid.append(document.importNode(c, true)));
      const nm = $('[data-more]', doc), wrap = m.closest('.more');
      if (nm) { m.href = nm.href; m.textContent = m.dataset.label || 'Show more products'; m.removeAttribute('aria-busy'); const nn = nm.closest('.more')?.querySelector('.note'); const on = wrap.querySelector('.note'); if (nn && on) on.textContent = nn.textContent; } else wrap.remove();
      const rn = $('.results-note'), drn = $('.results-note', doc); if (rn && drn) rn.textContent = rn.textContent.replace(/–[\d,]+/, '–' + (drn.textContent.match(/–([\d,]+)/) || [])[1]);
      history.replaceState(null, '', m.href.replace(/([?&])page=\d+/, '$1page=1'));
      fitAll(grid); syncControls(); markSaved(grid);
      if (!res) cards[0] && $('a.nm', grid.children[grid.children.length - cards.length])?.focus({ preventScroll: true });
    } catch (err) { location.href = m.href; }
  });

  function unitOf(card) { const per = parseFloat(card.dataset.per); return { per: isNaN(per) ? null : per, u: card.dataset.unit || '' }; }
  function pdp() {
    const P = $('[data-pdp]'); if (!P) return;
    // gallery
    const thumbs = $$('[data-thumb]'), gimg = $('.gimg'), cnt = $('[data-gcount]');
    let gi = 0;
    const show = i => { if (!thumbs.length || !gimg) return; gi = (i + thumbs.length) % thumbs.length; const t = thumbs[gi];
      gimg.classList.remove('ok'); gimg.removeAttribute('srcset'); gimg.dataset.src = t.dataset.src; gimg.dataset.w = t.dataset.w; gimg.dataset.h = t.dataset.h; gimg.src = t.dataset.full; delete gimg.dataset.fitted; gimg.dataset.max = 1000; fitAll(gimg.parentNode);
      thumbs.forEach((b, j) => b.setAttribute('aria-current', String(j === gi))); if (cnt) cnt.textContent = `${gi + 1} / ${thumbs.length}`; };
    thumbs.forEach((b, i) => b.addEventListener('click', () => show(i)));
    $$('[data-g]').forEach(b => b.addEventListener('click', () => show(gi + +b.dataset.g)));
    // quantity stepper
    const qi = $('#qty'), total = $('[data-total]'), big = $('[data-bigadd]');
    const updTotal = () => { if (total && big) total.textContent = money(+big.dataset.price * Math.max(1, +qi.value || 1)); };
    $$('[data-q]').forEach(b => b.addEventListener('click', () => { const max = +qi.max || 99; qi.value = Math.max(1, Math.min(max, (+qi.value || 1) + +b.dataset.q)); updTotal(); }));
    qi && qi.addEventListener('input', updTotal);
    // mobile sticky bar
    const mbar = $('[data-mbar]'), buy = $('#buyForm');
    if (mbar && 'IntersectionObserver' in window) new IntersectionObserver(es => mbar.classList.toggle('on', !es[0].isIntersecting && es[0].boundingClientRect.top < 0)).observe(buy || $('.bigtag'));
    $('[data-mbar-add]')?.addEventListener('click', e => add(P.dataset.variant, 1, e.currentTarget));
    // recently viewed
    const rv = store.get('ub-recent', []).filter(h => h !== P.dataset.handle);
    store.set('ub-recent', [P.dataset.handle, ...rv].slice(0, 12));
    // the shelf: neighbors, compare table, a kinder cheaper option, walk-the-shelf arrows
    const su = P.dataset.shelfUrl;
    if (su) fetchSection(su, 'shelf-row').then(h => {
      const cards = $$('[data-card]', html(h)); if (!cards.length) return;
      const meIdx = cards.findIndex(c => c.dataset.handle === P.dataset.handle);
      const myWords = words($('.bb h1')?.textContent);
      const rel = c => overlap(myWords, words($('.nm', c)?.textContent));
      // in stock first, then the most similar products
      const others = cards.filter(c => c.dataset.handle !== P.dataset.handle).sort((a, b) => (a.classList.contains('out') - b.classList.contains('out')) || (rel(b) - rel(a)));
      if (others.length) {
        const track = $('[data-shelf-track]'); others.slice(0, 16).forEach(c => track.append(document.importNode(c, true)));
        $('[data-shelf-section]').hidden = false; fitAll(track); wireRows(track.closest('.rowwrap').parentNode); syncControls();
      }
      const me = { per: parseFloat(P.dataset.per), u: P.dataset.unit };
      // walk the shelf
      const sw = $('[data-shelfwalk]');
      if (sw && cards.length > 1 && meIdx > -1) {
        const prev = cards[(meIdx - 1 + cards.length) % cards.length], next = cards[(meIdx + 1) % cards.length];
        const nm = c => $('.nm', c)?.textContent.trim();
        sw.innerHTML = `<a class="round" href="${$('a.nm', prev).getAttribute('href')}" aria-label="Previous on this shelf: ${esc(nm(prev))}"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></a><span class="num sw-t">${meIdx + 1} of ${cards.length}${cards.length >= 24 ? '+' : ''} on this shelf</span><a class="round" href="${$('a.nm', next).getAttribute('href')}" aria-label="Next on this shelf: ${esc(nm(next))}"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`;
        sw.hidden = false;
        document.addEventListener('keydown', e => { if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.target.matches('input,textarea,select,button,a,summary,[contenteditable]') || $('#storeMap')?.open || !$('[data-cart-drawer]')?.hidden) return; if (e.key === 'ArrowRight') location.href = $('a.round:last-child', sw).href; if (e.key === 'ArrowLeft') location.href = $('a.round', sw).href; });
      }
      // better value, same unit, at least 10% less
      if (me.per && me.u) {
        const alt = others.filter(c => !c.classList.contains('out')).map(c => ({ c, ...unitOf(c) })).filter(o => o.u === me.u && o.per && o.per < me.per * 0.9 && rel(o.c) > 0).sort((a, b) => a.per - b.per)[0];
        const n = $('[data-nudge]');
        if (alt && n) {
          const img = $('img', alt.c), url = $('a.nm', alt.c).getAttribute('href');
          n.innerHTML = `<span class="th">${img ? `<img src="${esc(img.dataset.src || img.src)}${(img.dataset.src || img.src).includes('?') ? '&' : '?'}width=128" alt="">` : ''}</span><span><b>Better value on this shelf</b>${esc(($('.br .bn', alt.c) || $('.br', alt.c)).textContent)} ${esc($('.nm', alt.c).textContent)} · ${money(Math.round(alt.per))}/${esc(me.u)} (${Math.round((1 - alt.per / me.per) * 100)}% less per ${esc(me.u)})</span><a class="btn" href="${url}">View</a>`;
          n.hidden = false;
        }
      }
      // compare table: this product and up to 3 neighbors
      const body = $('[data-compare-body]');
      if (body && others.length) {
        const similar = others.filter(c => rel(c) > 0);
        const rows = [cards[meIdx] || null, ...(similar.length ? similar : others).slice(0, 3)].filter(Boolean);
        const pers = rows.map(unitOf).filter(x => x.u === me.u && x.per);
        const best = pers.length > 1 ? Math.min(...pers.map(x => x.per)) : null;
        body.innerHTML = rows.map(c => {
          const isMe = c.dataset.handle === P.dataset.handle, u = unitOf(c), price = +c.dataset.price, pack = +c.dataset.pack || 1;
          const img = $('img', c), url = $('a.nm', c).getAttribute('href');
          const tag = $('.tg span', c)?.textContent || '';
          return `<tr class="${isMe ? 'me' : ''}"><td><div class="pn">${img ? `<img src="${esc(img.dataset.src || img.src)}${(img.dataset.src || img.src).includes('?') ? '&' : '?'}width=104" alt="">` : ''}<a href="${url}">${esc(($('.br .bn', c) || $('.br', c)).textContent)} ${esc($('.nm', c).textContent)}</a></div></td><td class="num">${esc(tag)}</td><td class="num">${money(price)}</td><td class="num">${money(Math.round(price / pack))}</td><td class="num">${u.u === me.u && u.per ? `${money(Math.round(u.per))}/${esc(u.u)}` : '–'}${best && u.per === best && u.u === me.u ? '<span class="best">Best value</span>' : ''}</td><td>${isMe ? '<b>Viewing</b>' : `<a class="btn" href="${url}">View</a>`}</td></tr>`;
        }).join('');
        $('[data-compare]').hidden = false;
      }
    }).catch(() => {});
    // Shopify recommendations
    const rec = $('[data-recs]');
    if (rec && !rec.querySelector('.rowtrack')) fetch(rec.dataset.url).then(r => r.text()).then(t => {
      const n = $('[data-recs]', html(t)); if (n && n.innerHTML.trim()) { rec.innerHTML = n.innerHTML; fitAll(rec); wireRows(rec); syncControls(); } else rec.remove();
    }).catch(() => rec.remove());
  }

  /* fetch product cards a few at a time, so a long list never floods the store with requests */
  async function cardsInto(target, handles, each = 4) {
    const one = h => fetch(`${UB.routes.root}products/${encodeURIComponent(h)}?view=card`).then(r => r.ok ? r.text() : '').catch(() => '');
    for (let k = 0; k < handles.length; k += each) {
      const parts = await Promise.all(handles.slice(k, k + each).map(one));
      parts.forEach(p => { const c = $('[data-card]', html(p)); if (c) target.append(c); });
      fitAll(target); markSaved(target); syncControls();
    }
  }
  /* ---------- recently viewed ---------- */
  async function recent() {
    const sec = $('[data-recent-section]'); if (!sec) return;
    const me = $('[data-pdp]')?.dataset.handle;
    const hs = store.get('ub-recent', []).filter(h => h !== me).slice(0, 10);
    if (hs.length < 3) return;                                          // one or two items would leave a mostly empty band
    const track = $('[data-recent-track]', sec);
    await cardsInto(track, hs);
    if (track.children.length < 3) return;
    sec.hidden = false; fitAll(track); wireRows(sec); syncControls(); markSaved(track);
    $('[data-recent-clear]', sec)?.addEventListener('click', () => { store.set('ub-recent', []); sec.hidden = true; });
  }

  /* ---------- side panels share the scrim ---------- */
  const scrim = on => { const s = $('[data-scrim]'); if (s) s.hidden = !on; document.documentElement.style.overflow = on ? 'hidden' : ''; };

  /* ---------- departments menu (loaded on first open) ---------- */
  const mega = $('[data-mega]'), deptBtns = $$('[data-dept]'), deptBtn = deptBtns.find(b => b.offsetParent !== null) || deptBtns[0];
  const setDeptExpanded = v => deptBtns.forEach(b => b.setAttribute('aria-expanded', v));
  let megaLoaded = false;
  function setAisle(li) { $$('.mm-a', mega).forEach(a => a.classList.toggle('on', a === li)); }
  async function openMega() {
    if (!mega) return;
    closeTA(); closeDrawer(); closeSaved();
    mega.hidden = false; setDeptExpanded('true');
    if (!megaLoaded) {
      megaLoaded = true;
      mega.innerHTML = '<p class="mm-loading">Opening the departments…</p>';
      try {
        const h = await fetchSection(UB.routes.root, 'mega-menu');
        const mm = $('[data-mm]', html(h));
        if (mm) { mega.innerHTML = ''; mega.append(mm); fitAll(mega); if (innerWidth > 900) setAisle($('.mm-a', mega)); }
      } catch (e) { megaLoaded = false; mega.innerHTML = `<p class="mm-loading">Could not open the menu. <a href="${UB.routes.root}collections">See all departments</a></p>`; }
    }
    if (innerWidth <= 900) { document.documentElement.style.overflow = 'hidden'; asDialog(mega, 'Departments'); } else if (mega.getAttribute('role') === 'dialog') { mega.removeAttribute('role'); mega.removeAttribute('aria-modal'); }
    ($('.mm-al', mega) || mega).focus?.();
  }
  function closeMega(refocus) {
    if (!mega || mega.hidden) return;
    mega.hidden = true; setDeptExpanded('false');
    if (innerWidth <= 900) document.documentElement.style.overflow = '';
    if (refocus) (deptBtns.find(b => b.offsetParent !== null) || deptBtn)?.focus();
  }
  if (mega && deptBtn) {
    deptBtns.forEach(b => {
      b.addEventListener('click', () => mega.hidden ? openMega() : closeMega());
      b.addEventListener('mouseenter', () => { if (innerWidth > 900 && !megaLoaded) fetchSection(UB.routes.root, 'mega-menu').catch(() => {}); }, { once: true });
    });
    mega.addEventListener('mouseover', e => { if (innerWidth <= 900) return; const li = e.target.closest('.mm-a'); if (li && !li.classList.contains('on')) setAisle(li); });
    mega.addEventListener('focusin', e => { if (innerWidth <= 900) return; const li = e.target.closest('.mm-a'); if (li && e.target.matches('.mm-al')) setAisle(li); });
    mega.addEventListener('click', e => {
      if (e.target.closest('[data-dept-close]')) { closeMega(true); return; }
      const al = e.target.closest('.mm-al');
      if (al && innerWidth <= 900) { const li = al.closest('.mm-a'); if (!li.classList.contains('on')) { e.preventDefault(); setAisle(li); } }
    });
    document.addEventListener('click', e => { if (!mega.hidden && !e.target.closest('[data-mega]') && !e.target.closest('[data-dept]')) closeMega(); });
  }

  /* ---------- saved items (this device) ---------- */
  const SAVED = 'ub-saved';
  const savedList = () => store.get(SAVED, []);
  function markSaved(root = document) {
    const s = new Set(savedList());
    $$('[data-save]', root).forEach(b => {
      const on = s.has(b.dataset.save);
      b.setAttribute('aria-pressed', String(on));
      const nm = b.getAttribute('aria-label').replace(/^(Save|Remove) /, '').replace(/ from saved items$/, '');
      b.setAttribute('aria-label', on ? `Remove ${nm} from saved items` : `Save ${nm}`);
    });
    $$('[data-saved-count]').forEach(c => { c.textContent = s.size; c.hidden = !s.size; });
  }
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-save]'); if (!b) return;
    e.preventDefault();
    const h = b.dataset.save; let l = savedList();
    const on = l.includes(h);
    l = on ? l.filter(x => x !== h) : [h, ...l].slice(0, 60);
    store.set(SAVED, l); markSaved();
    if (!on) toastMsg('Saved. Find it under My items.');
    if (on && b.closest('[data-saved-list]')) { b.closest('[data-card]')?.remove(); savedEmpty(); }
  });
  function savedEmpty() { const list = $('[data-saved-list]'), n = list ? list.children.length : 0; const em = $('[data-saved-empty]'); if (em) em.hidden = n > 0; const sn = $('[data-saved-n]'); if (sn) sn.textContent = n ? `(${n})` : ''; }
  async function openSaved() {
    const d = $('[data-saved-drawer]'); if (!d) return;
    closeDrawer(); closeMega(); lastFocus = document.activeElement; asDialog(d);
    scrim(true); d.hidden = false; d.focus();
    const list = $('[data-saved-list]', d); const hs = savedList();
    list.innerHTML = ''; savedEmpty();
    if (!hs.length) return;
    await cardsInto(list, hs.slice(0, 48));
    savedEmpty();
  }
  function closeSaved() { const d = $('[data-saved-drawer]'); if (!d || d.hidden) return; d.hidden = true; scrim(false); if (lastFocus) lastFocus.focus(); }
  document.addEventListener('click', e => { if (e.target.closest('[data-saved-open]')) { e.preventDefault(); openSaved(); } });

  /* ---------- front window slides ---------- */
  function carousel() {
    $$('[data-carousel]').forEach(c => {
      const slides = $$('.slide', c), dots = $$('.dot', c); if (slides.length < 2) return;
      let i = 0, timer = null, hold = false;
      const go = n => {
        i = (n + slides.length) % slides.length;
        slides.forEach((s, k) => { const on = k === i; s.classList.toggle('on', on); s.setAttribute('aria-hidden', String(!on)); $$('a,button', s).forEach(a => on ? a.removeAttribute('tabindex') : a.setAttribute('tabindex', '-1')); });
        dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
        c.classList.toggle('is-dark', slides[i].classList.contains('dark'));
        fitAll(slides[i]);
      };
      c.addEventListener('click', e => { const d = e.target.closest('[data-go]'), a = e.target.closest('[data-slide]'); if (d) go(+d.dataset.go); if (a) go(i + +a.dataset.slide); });
      // a finger swipes between slides
      let tx = null;
      c.addEventListener('touchstart', e => { tx = e.touches[0].clientX; }, { passive: true });
      c.addEventListener('touchend', e => { if (tx == null) return; const dx = e.changedTouches[0].clientX - tx; tx = null; if (Math.abs(dx) > 45) go(i + (dx < 0 ? 1 : -1)); }, { passive: true });
      const ms = +c.dataset.autoplay;
      if (ms && !REDUCED) {
        ['mouseenter', 'focusin'].forEach(ev => c.addEventListener(ev, () => hold = true));
        ['mouseleave', 'focusout'].forEach(ev => c.addEventListener(ev, () => hold = false));
        let stopped = false;
        const pz = document.createElement('button'); pz.type = 'button'; pz.className = 'round cpause'; pz.setAttribute('aria-label', 'Pause slides'); pz.setAttribute('aria-pressed', 'false');
        pz.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/></svg>';
        pz.addEventListener('click', () => { stopped = !stopped; pz.setAttribute('aria-pressed', String(stopped)); pz.setAttribute('aria-label', stopped ? 'Play slides' : 'Pause slides'); pz.firstChild.innerHTML = stopped ? '<path d="M8 5l11 7-11 7z" fill="currentColor"/>' : '<path d="M7 5h4v14H7zM13 5h4v14h-4z" fill="currentColor"/>'; });
        $('.dots', c)?.append(pz);
        timer = setInterval(() => { if (!hold && !stopped && document.visibilityState === 'visible') go(i + 1); }, ms);
      }
    });
  }

  /* ---------- popular in every aisle: tabs ---------- */
  function tabs() {
    $$('[data-tabs]').forEach(tl => {
      const panel = $('[data-tabpanel]', tl.parentNode), all = $('[data-tab-all]', tl.closest('section')), cache = {};
      cache[$('[aria-selected="true"]', tl)?.dataset.url] = panel.innerHTML;
      const pick = async b => {
        $$('[role=tab]', tl).forEach(t => { const on = t === b; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; });
        panel.setAttribute('aria-labelledby', b.id);
        if (all) all.href = b.dataset.url;
        const u = b.dataset.url;
        if (!cache[u]) {
          panel.setAttribute('aria-busy', 'true');
          try { const h = await fetchSection(u, 'collection-row'); const r = $('[data-crow]', html(h)); cache[u] = r ? r.innerHTML : ''; } catch (e) { cache[u] = `<p class="note"><a class="link" href="${u}">Open the aisle</a></p>`; }
          panel.removeAttribute('aria-busy');
        }
        if ($('[aria-selected="true"]', tl) !== b) return;
        panel.innerHTML = cache[u]; fitAll(panel); wireRows(panel); syncControls(); markSaved(panel);
      };
      tl.addEventListener('click', e => { const b = e.target.closest('[role=tab]'); if (b) pick(b); });
      tl.addEventListener('keydown', e => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const ts = $$('[role=tab]', tl), k = ts.indexOf(document.activeElement); if (k < 0) return;
        const n = ts[(k + (e.key === 'ArrowRight' ? 1 : -1) + ts.length) % ts.length]; n.focus(); pick(n); n.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    });
  }

  /* ---------- aisle filters as a drawer on phones ---------- */
  function openFside() { const f = $('[data-fside]'); if (!f) return; lastFocus = document.activeElement; asDialog(f, 'Filters'); f.classList.add('on'); scrim(true); $('.fside-head button', f)?.focus(); }
  function closeFside() { const f = $('[data-fside]'); if (!f || !f.classList.contains('on')) return; f.classList.remove('on'); scrim(false); if (lastFocus) lastFocus.focus(); }
  document.addEventListener('click', e => { if (e.target.closest('[data-fside-open]')) { e.preventDefault(); openFside(); } if (e.target.closest('[data-fside-close]')) { e.preventDefault(); closeFside(); } });

  /* ---------- shelf circles that need a picture ---------- */
  function shelfThumbs() {
    const els = $$('[data-shelf-thumb]'); if (!els.length) return;
    const load = el => fetchSection(el.dataset.shelfThumb, 'shelf-thumb').then(h => { const pk = $('.pk', html(h)); if (pk) { el.append(pk); fitAll(el); } else el.closest('li')?.remove(); }).catch(() => {});
    if (!('IntersectionObserver' in window)) return els.forEach(load);
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { io.unobserve(e.target); load(e.target); } }), { rootMargin: '200px' });
    els.forEach(el => io.observe(el));
  }

  /* ---------- brands A-Z: find as you type ---------- */
  function brands() {
    const inp = $('[data-brand-find]'); if (!inp) return;
    const items = $$('.bgroup li'), groups = $$('.bgroup'), none = $('[data-brand-none]');
    const norm = t => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ'’.]/g, '');
    items.forEach(li => li._t = norm(li.textContent));
    inp.addEventListener('input', () => {
      const q = norm(inp.value.trim());
      items.forEach(li => li.hidden = !!q && !li._t.includes(q));
      let any = false; groups.forEach(g => { const vis = $$('li:not([hidden])', g).length > 0; g.hidden = !vis; any = any || vis; });
      if (none) none.hidden = any;
    });
  }

  /* ---------- deal countdown ---------- */
  function countdown() {
    $$('[data-countdown]').forEach(el => {
      const end = new Date(el.dataset.countdown).getTime(); if (!end) return;
      const tick = () => { const s = Math.max(0, Math.floor((end - Date.now()) / 1000)); if (!s) { el.textContent = 'Deal ended'; return; } const d = Math.floor(s / 86400), h = Math.floor(s % 86400 / 3600), m = Math.floor(s % 3600 / 60); el.textContent = `Ends in ${d ? d + 'd ' : ''}${h}h ${m}m`; el.hidden = false; setTimeout(tick, 30000); };
      tick();
    });
  }

  /* ---------- phone header: hide the quick links while scrolling down ---------- */
  function compactHeader() {
    const sh = $('#hdr')?.closest('.sec-header'), row = $('#hdr .row'), sn = $('.subnav'); if (!sh) return;
    let y = scrollY, on = false, tick = false;
    const size = () => {
      const lr = $('#hdr .logo')?.closest('.row'), s = $('#searchForm');
      if (!lr || !s) return;
      const top = s.getBoundingClientRect().top - sh.getBoundingClientRect().top;
      sh.style.setProperty('--hdr-lift', Math.max(0, Math.round(top - 6)) + 'px');
      sh.style.setProperty('--hdr-clip', Math.round(sh.offsetHeight - (s.getBoundingClientRect().bottom - sh.getBoundingClientRect().top) - 8) + 'px');
    };
    const set = v => { if (v === on) return; on = v; if (v) size(); sh.classList.toggle('compact', v); document.documentElement.classList.toggle('hdr-min', v); };
    addEventListener('scroll', () => {
      if (tick) return; tick = true;
      requestAnimationFrame(() => {
        tick = false; const ny = scrollY, d = ny - y;
        if (document.activeElement === q) { y = ny; return; }
        if (innerWidth > 680 || ny < 120) set(false);
        else if (d > 6) set(true);
        else if (d < -10) set(false);
        if (Math.abs(d) > 6) y = ny;
      });
    }, { passive: true });
    addEventListener('resize', () => on && size(), { passive: true });
    UB.showHeader = () => set(false);
  }

  /* ---------- small touches ---------- */
  function greet() {
    const g = $('[data-greet]'); if (!g) return;
    const h = new Date().getHours();
    g.textContent = h < 5 ? 'Good evening.' : h < 12 ? 'Good morning.' : h < 18 ? 'Good afternoon.' : 'Good evening.';
  }
  function hdrH() { const h = $('#hdr')?.closest('.sec-header') || $('#hdr'); if (h && h.offsetHeight) document.documentElement.style.setProperty('--hdr-h', h.offsetHeight + 'px'); }

  /* helpers the front page script (universo-home.js) builds on */
  Object.assign(UB, { fitAll, wireRows, markSaved, syncControls, fetchSection, html, addMany, openDrawer });

  function init() {
    [wall, fitAll, wireRows, pdp, recent, greet, hdrH, carousel, tabs, shelfThumbs, brands, countdown, compactHeader, markSaved].forEach(f => { try { f(); } catch (e) {} });
    addEventListener('resize', () => { hdrH(); wireRows(); }, { passive: true });
    const n = +($('#cartCount')?.textContent || 0);
    if (n > 0) loadCart();
  }
  addEventListener('pageshow', e => { if (!e.persisted) return; loadCart().then(c => { if (c) { const b = $('#cartCount'); if (b) { b.textContent = c.item_count; b.hidden = !c.item_count; } } }); markSaved(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
